import { z } from 'zod';

/**
 * Every API error is an RFC 9457 problem document (Content-Type: application/problem+json)
 * with a stable `code` that clients switch on. Messages are written for people; codes for code.
 */
export const PROBLEM_CODES = [
  'VALIDATION_FAILED',
  'UNAUTHENTICATED',
  // Sign-in (Phase 1). TOKEN_EXPIRED: refresh the Firebase token and retry; the others: sign in again.
  'TOKEN_MISSING',
  'TOKEN_INVALID',
  'TOKEN_EXPIRED',
  'TOKEN_REVOKED',
  'ACCOUNT_DISABLED',
  'PHONE_NOT_SUPPORTED',
  'CONSENT_VERSION_UNKNOWN',
  // Chat (Phase 2).
  'QUESTION_CLOSED',
  'CHAT_BUSY',
  // Businesses (Phase 3).
  'GSTIN_INVALID',
  'GSTIN_NOT_FOUND',
  'GSTIN_CANCELLED',
  'PAN_INVALID',
  'BUSINESS_EXISTS',
  'REGISTER_UNAVAILABLE',
  'FEATURE_OFF',
  // The GST portal connection (Phase 4).
  'GST_API_ACCESS_OFF',
  'GST_USERNAME_INVALID',
  'GST_PORTAL_UNAVAILABLE',
  'GST_PORTAL_REFUSED',
  'GST_NOT_CONNECTED',
  'RECONNECT_NEEDED',
  'OTP_NOT_SENT',
  'OTP_WRONG',
  'OTP_EXPIRED',
  'OTP_LOCKED',
  // GSTR-1 sales (Phase 6).
  'RETURN_FILED',
  'INVOICE_NUMBER_TAKEN',
  'RATE_NOT_ALLOWED',
  'VERSION_MISMATCH',
  'VERSION_REQUIRED',
  'ISSUE_CLOSED',
  // Filing GSTR-1 (Phase 9).
  'RETURN_BEING_FILED',
  'FILING_NOT_READY',
  'SUMMARY_CHANGED',
  'FILING_NOT_CONFIRMED',
  'FILING_STEP',
  'SIGNATORY_NEEDED',
  'SIGNATORY_INVALID',
  // GSTR-3B (Phase 10).
  'GSTR1_FIRST',
  'GSTR3B_EARLIER',
  'GSTR2B_NOT_READY',
  'GSTR3B_NOT_READ',
  'REVERSAL_TOO_LARGE',
  // Paying and filing GSTR-3B (Phase 11).
  'CHALLAN_PENDING',
  'CHALLAN_EXPIRED',
  'NOTHING_TO_PAY',
  'CASH_SHORT',
  // Receipts, reminders and notifications (Phase 13).
  'PUSH_NOT_SET_UP',
  'LINK_INVALID',
  // People, invites and approvals (Phase 14). NOT_IN_YET: added, but not in yet (connect the GST account,
  // or be invited); OWNER_ONLY: staff can't do it; LAST_OWNER: a business keeps at least one owner.
  'NOT_IN_YET',
  'OWNER_ONLY',
  'LAST_OWNER',
  // Gap 2, the admin console. REAUTH_NEEDED: this needs a sign-in from the last 10 minutes (confirm it's
  // you); LAST_ADMIN: the last admin can't be removed, nor can admins remove themselves.
  'REAUTH_NEEDED',
  'LAST_ADMIN',
  'ALREADY_IN',
  'INVITE_EXPIRED',
  'INVITE_USED',
  'INVITE_CANCELLED',
  'INVITE_NOT_FOR_YOU',
  'APPROVAL_PENDING',
  'APPROVAL_CLOSED',
  'RETURN_CHANGED',
  // No records (Phase 16). WALK_IN_SALES: walk-in sales are changed on their own page; STATEMENT_LOCKED: the
  // statement isn't waiting for a password (or is); PASSWORD_WRONG: it didn't open the file (with tries left);
  // NOTHING_TO_DECIDE: that group of payments needs no answer.
  'WALK_IN_SALES',
  'STATEMENT_LOCKED',
  'PASSWORD_WRONG',
  'NOTHING_TO_DECIDE',
  // Gap 4, complete GSTR-1. NOTE_TOO_LATE: a credit note on a sale whose year closed (after 30 November).
  'NOTE_TOO_LATE',
  // GSTR-1A (gap 4b): it isn't open for the month now (GSTR-1 not filed, not its due date yet, GSTR-3B filed or
  // GSTR-1A filed already); confirming it needs GSTR-3B's figures agreed to.
  'GSTR1A_CLOSED',
  'DECLARATION_NEEDED',
  // Privacy and data rights (Phase 17). ACCOUNT_DELETING: the account is set to be deleted (keep it first);
  // NOT_DELETING: nothing to keep; EXPORT_RUNNING: a download is being made already (one at a time);
  // EXPORT_NOT_READY: it isn't made yet, or has expired; CONSENT_NEEDED: allow it first (invoice photos read by AI).
  'ACCOUNT_DELETING',
  'NOT_DELETING',
  'EXPORT_RUNNING',
  'EXPORT_NOT_READY',
  'CONSENT_NEEDED',
  // Uploads and imports (Phase 7).
  'FILE_TYPE_NOT_ALLOWED',
  'FILE_TOO_BIG',
  'UPLOAD_MISSING',
  'UPLOAD_NOT_READY',
  'UPLOAD_REJECTED',
  'IMPORT_NOT_READY',
  'PROBLEM_CLOSED',
  'FORBIDDEN',
  'NOT_FOUND',
  'METHOD_NOT_ALLOWED',
  'CONFLICT',
  'PAYLOAD_TOO_LARGE',
  'UNSUPPORTED_MEDIA_TYPE',
  'RATE_LIMITED',
  // Firebase App Check (APP_CHECK_ENFORCE): the call didn't come from EkApp's own web app.
  'APP_CHECK_FAILED',
  'IDEMPOTENCY_KEY_REQUIRED',
  'IDEMPOTENCY_KEY_INVALID',
  'IDEMPOTENCY_KEY_REUSED',
  'IDEMPOTENCY_IN_PROGRESS',
  'SERVICE_UNAVAILABLE',
  'INTERNAL',
] as const;

export type ProblemCode = (typeof PROBLEM_CODES)[number];

export const ProblemDetailsSchema = z.object({
  type: z.string(),
  title: z.string(),
  status: z.number().int(),
  code: z.string(),
  detail: z.string().optional(),
  requestId: z.string().optional(),
  /** Field path → message, for VALIDATION_FAILED. */
  fields: z.record(z.string(), z.string()).optional(),
  /** VALIDATION_FAILED: a value that would do, to offer instead (a photo check's GSTIN). */
  suggestion: z.string().optional(),
  /** Seconds to wait before retrying (also sent as the Retry-After header). */
  retryAfterSec: z.number().int().optional(),
  /** OTP_WRONG: how many more codes can be tried. */
  attemptsLeft: z.number().int().optional(),
  /** GSTR1_FIRST, GSTR3B_EARLIER: the month (YYYY-MM) that comes first. */
  period: z.string().optional(),
  /** OTP_LOCKED: when trying again is allowed (ISO time). */
  lockedUntil: z.string().optional(),
});

export type ProblemDetails = z.infer<typeof ProblemDetailsSchema>;

export const problemType = (code: string): string => `urn:oneapp:problem:${code.toLowerCase().replace(/_/g, '-')}`;

export function isProblemDetails(value: unknown): value is ProblemDetails {
  return ProblemDetailsSchema.safeParse(value).success;
}
